import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  Activity,
  CheckCircle,
  XCircle,
  Clock,
  RefreshCw,
  ChevronDown,
  Loader2,
  AlertCircle
} from 'lucide-react';
import { format } from 'date-fns';

interface WebhookDeliveryLog {
  id: string;
  webhook_id: string;
  event_type: string;
  payload: any;
  response_status: number | null;
  response_body: string | null;
  attempt_number: number;
  max_attempts: number;
  next_retry_at: string | null;
  status: string;
  error_message: string | null;
  duration_ms: number | null;
  created_at: string;
  completed_at: string | null;
  webhooks?: {
    url: string;
  } | null;
}

export const WebhookDeliveryLogs = () => {
  const { user } = useAuth();
  const [logs, setLogs] = useState<WebhookDeliveryLog[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedLogs, setExpandedLogs] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (user) {
      fetchLogs();
    }
  }, [user]);

  const fetchLogs = async () => {
    try {
      const { data, error } = await supabase
        .from('webhook_delivery_logs')
        .select(`
          *,
          webhooks (url)
        `)
        .order('created_at', { ascending: false })
        .limit(100);

      if (error) throw error;
      setLogs((data as WebhookDeliveryLog[]) || []);
    } catch (error) {
      console.error('Error fetching webhook logs:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleExpanded = (logId: string) => {
    setExpandedLogs(prev => {
      const newSet = new Set(prev);
      if (newSet.has(logId)) {
        newSet.delete(logId);
      } else {
        newSet.add(logId);
      }
      return newSet;
    });
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'failed':
        return <XCircle className="h-4 w-4 text-red-500" />;
      case 'retrying':
        return <RefreshCw className="h-4 w-4 text-yellow-500 animate-spin" />;
      case 'pending':
        return <Clock className="h-4 w-4 text-muted-foreground" />;
      default:
        return <AlertCircle className="h-4 w-4 text-muted-foreground" />;
    }
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, string> = {
      success: 'bg-green-500/10 text-green-500 border-green-500/20',
      failed: 'bg-red-500/10 text-red-500 border-red-500/20',
      retrying: 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20',
      pending: 'bg-muted text-muted-foreground'
    };
    return variants[status] || variants.pending;
  };

  const getHttpStatusColor = (status: number | null) => {
    if (!status) return 'bg-gray-500';
    if (status >= 200 && status < 300) return 'bg-green-500';
    if (status >= 400 && status < 500) return 'bg-yellow-500';
    if (status >= 500) return 'bg-red-500';
    return 'bg-gray-500';
  };

  if (isLoading) {
    return (
      <Card className="card-elegant">
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="card-elegant">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5 text-primary" />
              Webhook Delivery Logs
            </CardTitle>
            <CardDescription>
              View the history of webhook deliveries with retry status
            </CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={fetchLogs}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {logs.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <Activity className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No webhook deliveries yet</p>
            <p className="text-sm">Webhook logs will appear here when forms receive submissions</p>
          </div>
        ) : (
          <ScrollArea className="h-[600px] pr-4">
            <div className="space-y-3">
              {logs.map((log) => (
                <Collapsible
                  key={log.id}
                  open={expandedLogs.has(log.id)}
                  onOpenChange={() => toggleExpanded(log.id)}
                >
                  <div className="border rounded-lg overflow-hidden">
                    <CollapsibleTrigger asChild>
                      <button className="w-full p-4 flex items-center justify-between hover:bg-muted/50 transition-colors">
                        <div className="flex items-center gap-3">
                          {getStatusIcon(log.status)}
                          <div className="text-left">
                            <div className="flex items-center gap-2">
                              <Badge variant="outline" className={getStatusBadge(log.status)}>
                                {(log.status?.charAt(0) || "").toUpperCase() + (log.status?.slice(1) || "")}
                              </Badge>
                              <Badge variant="secondary">{log.event_type}</Badge>
                              {log.response_status && (
                                <Badge className={`${getHttpStatusColor(log.response_status)} text-white`}>
                                  {log.response_status}
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground mt-1 truncate max-w-md">
                              {log.webhooks?.url || 'Unknown URL'}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right text-sm">
                            <p className="text-muted-foreground">
                              {format(new Date(log.created_at), 'MMM d, HH:mm:ss')}
                            </p>
                            <div className="flex items-center gap-2 text-xs text-muted-foreground">
                              {log.duration_ms && (
                                <span>{log.duration_ms}ms</span>
                              )}
                              <span>Attempt {log.attempt_number}/{log.max_attempts}</span>
                            </div>
                          </div>
                          <ChevronDown className={`h-4 w-4 transition-transform ${expandedLogs.has(log.id) ? 'rotate-180' : ''}`} />
                        </div>
                      </button>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <div className="border-t p-4 space-y-4 bg-muted/30">
                        {/* Error Message */}
                        {log.error_message && (
                          <div className="p-3 rounded bg-red-500/10 border border-red-500/20">
                            <p className="text-sm font-medium text-red-500 mb-1">Error</p>
                            <p className="text-sm text-red-400">{log.error_message}</p>
                          </div>
                        )}

                        {/* Retry Info */}
                        {log.status === 'retrying' && log.next_retry_at && (
                          <div className="p-3 rounded bg-yellow-500/10 border border-yellow-500/20">
                            <p className="text-sm font-medium text-yellow-500 mb-1">Next Retry</p>
                            <p className="text-sm text-yellow-400">
                              Scheduled for {format(new Date(log.next_retry_at), 'MMM d, HH:mm:ss')}
                            </p>
                          </div>
                        )}

                        {/* Request Payload */}
                        <div>
                          <p className="text-sm font-medium mb-2">Request Payload</p>
                          <div className="bg-muted rounded-lg p-3 overflow-auto max-h-40">
                            <pre className="text-xs font-mono whitespace-pre-wrap">
                              {JSON.stringify(log.payload, null, 2)}
                            </pre>
                          </div>
                        </div>

                        {/* Response Body */}
                        {log.response_body && (
                          <div>
                            <p className="text-sm font-medium mb-2">Response Body</p>
                            <div className="bg-muted rounded-lg p-3 overflow-auto max-h-40">
                              <pre className="text-xs font-mono whitespace-pre-wrap">
                                {log.response_body}
                              </pre>
                            </div>
                          </div>
                        )}

                        {/* Timing Info */}
                        <div className="flex gap-4 text-xs text-muted-foreground">
                          <span>Created: {format(new Date(log.created_at), 'PPpp')}</span>
                          {log.completed_at && (
                            <span>Completed: {format(new Date(log.completed_at), 'PPpp')}</span>
                          )}
                        </div>
                      </div>
                    </CollapsibleContent>
                  </div>
                </Collapsible>
              ))}
            </div>
          </ScrollArea>
        )}
      </CardContent>
    </Card>
  );
};